import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import { logActivity } from "@/shared/utils/activityLogger";

export interface UserRelationshipData {
  relationship: string;
  isLoading: boolean;
  updateRelationship: (relationship: string) => Promise<void>;
  isUpdating: boolean;
}

export function useUserRelationship(): UserRelationshipData {
  const { user, person } = useAppContext();
  const queryClient = useQueryClient();
  const userId = user?.user_id;
  const estateId = person?.estate_id;
  const userEmail = user?.email;

  const { data: relationship, isLoading } = useQuery({
    queryKey: ["userRelationship", userId, estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_estates")
        .select("relationship_to_decedent")
        .eq("user_id", userId!)
        .eq("estate_id", estateId!)
        .maybeSingle();

      if (error) throw error;
      return data?.relationship_to_decedent ?? "";
    },
    enabled: !!userId && !!estateId,
    staleTime: 5 * 60 * 1000,
  });

  const { mutateAsync: updateRelationship, isPending: isUpdating } = useMutation({
    mutationFn: async (newRelationship: string) => {
      if (!userId || !estateId) return;

      const { error: relationshipError } = await supabase
        .from("user_estates")
        .update({ relationship_to_decedent: newRelationship || null })
        .eq("user_id", userId)
        .eq("estate_id", estateId);

      if (relationshipError) throw relationshipError;

      // Sync to estate_contacts for People tab display
      if (userEmail) {
        await supabase
          .from("estate_contacts")
          .update({ relationship: newRelationship || null })
          .eq("estate_id", estateId)
          .eq("email", userEmail);
      }

      if (userId) {
        logActivity({
          estate_id: estateId,
          action_type: "updated_relationship",
          user_id: userId,
          entity_type: "user",
          entity_id: String(userId),
          metadata: { relationship: newRelationship || null },
        }).catch(() => {});
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userRelationship", userId, estateId] });
      queryClient.invalidateQueries({ queryKey: ["person-contacts"] });
    },
  });

  return {
    relationship: relationship ?? "",
    isLoading,
    updateRelationship,
    isUpdating,
  };
}
